// slice-3562 (BigList #27) — the runtime half of the PB accelerator registry.
//
// PowerBuilder has ONE key dispatcher for the whole app (`w_phfc_base::key`,
// `phfc_system_ancestors/w_phfc_base.srw:285-323`): the window sees the keystroke and fans it
// out to an event, and a screen that wants different behaviour OVERRIDES that event. 10 routed
// windows do exactly that with `ue_fkey10/11/12`.
//
// PH10 mirrors the shape instead of copying PB's plumbing:
//   * ONE window keydown listener, installed on the first registration and torn down on the
//     last, so a screen that binds nothing costs nothing.
//   * Callers register an ACTION ('refresh'), never a key. The key for that action lives in
//     `pbShortcuts.ts` with its PB citation, so a substitute (F5 -> Alt+R) is decided once and
//     every toolbar tooltip picks it up for free.
//   * Handlers form a STACK per action: the most recently registered wins, which is PB's
//     override semantics (a modal that opens over a page takes the page's Save while it is
//     open and hands it straight back on close) and also the only ordering that survives
//     React's mount/unmount interleaving.
//     One precise caveat, pinned by `useShortcutAction.test.tsx`: React runs CHILD effects
//     BEFORE parent effects, so a child that mounts in the SAME commit as its parent
//     registers first and sits UNDER it. "Innermost wins" holds only for a child that mounts
//     later — which is the modal case. Nothing depends on the other ordering: a real modal
//     carries role="dialog" and guard rail 3 below already suppresses the page's
//     blockedByDialog actions while one is open.
//
// Guard rails, in the order the dispatcher applies them:
//   1. A combo nobody registered is left entirely alone — no preventDefault. If no page binds
//      Save, Ctrl+S keeps opening the browser's "Save Page As", which is strictly better than
//      swallowing the key and doing nothing.
//   2. `allowInEditor: false` shortcuts do not fire while focus is in a text field or an open
//      AG-Grid cell editor, so native text editing (Ctrl+Z, Insert, Ctrl+Del, Space) is never
//      hijacked mid-type.
//   3. `blockedByDialog: true` shortcuts do not fire while a modal is open — a page behind a
//      modal must not be saved or refreshed out from under it. App-chrome keys (F1/F9) are
//      deliberately exempt: PB's Help accelerators fire from anywhere.
//   4. A disabled registration is skipped, matching PB's `of_is_menu_enabled()` gate
//      (`uo_phfc_dwdefault.sru:1269`) — a greyed toolbar button's key must be inert too.
import {
  PB_SHORTCUTS,
  pbShortcut,
  type PbShortcut,
  type ShortcutActionId,
} from './pbShortcuts';

export interface ShortcutHandlerOptions {
  /**
   * False = registered but inert (the toolbar button is greyed). PB gates its accelerators
   * the same way via `of_is_menu_enabled()`. Defaults to true.
   */
  readonly enabled?: boolean;
}

interface Registration {
  readonly action: ShortcutActionId;
  readonly run: (event: KeyboardEvent) => void;
  enabled: boolean;
}

/** Handler stacks, one per action. Last entry wins. */
const stacks = new Map<ShortcutActionId, Registration[]>();

interface ParsedCombo {
  readonly ctrl: boolean;
  readonly alt: boolean;
  readonly shift: boolean;
  readonly base: string;
}

/** `'Ctrl+Alt+Shift+F2'` -> flags + base key. Order-insensitive, case-insensitive. */
export function parseCombo(combo: string): ParsedCombo {
  let ctrl = false;
  let alt = false;
  let shift = false;
  let base = '';
  for (const raw of combo.split('+')) {
    const part = raw.trim();
    const lower = part.toLowerCase();
    if (lower === 'ctrl' || lower === 'control') ctrl = true;
    else if (lower === 'alt') alt = true;
    else if (lower === 'shift') shift = true;
    else base = part;
  }
  return { ctrl, alt, shift, base };
}

/**
 * Does this keystroke mean {@link combo}?
 *
 * <p>Two browser quirks shaped the base-key test. Alt+letter on several keyboard layouts
 * reports a punctuation/dead `key` ("®" for Alt+R on macOS), so a physical `code` match is
 * accepted as well as a `key` match. And jsdom's `fireEvent.keyDown` supplies no `code` at
 * all, so neither test may be the only one.
 */
export function comboMatches(combo: string, e: KeyboardEvent): boolean {
  const { ctrl, alt, shift, base } = parseCombo(combo);
  // Cmd stands in for Ctrl so the fleet is usable on a Mac; PB itself is Windows-only.
  if (ctrl !== (e.ctrlKey || e.metaKey)) return false;
  if (alt !== e.altKey) return false;
  if (shift !== e.shiftKey) return false;

  const key = e.key ?? '';
  const code = e.code ?? '';
  if (base.length === 1 && /[a-z0-9]/i.test(base)) {
    const upper = base.toUpperCase();
    const wanted = /[0-9]/.test(base) ? `Digit${base}` : `Key${upper}`;
    return key.toUpperCase() === upper || code === wanted;
  }
  if (base.toLowerCase() === 'space') return key === ' ' || code === 'Space';
  return key.toLowerCase() === base.toLowerCase() || code.toLowerCase() === base.toLowerCase();
}

/** Every combo (primary + `alsoAccepts`) that resolves to a dispatchable action. */
const COMBO_TABLE: readonly { readonly combo: string; readonly shortcut: PbShortcut }[] =
  PB_SHORTCUTS.filter((s) => s.key !== null && !s.nativeOnly).flatMap((s) => [
    { combo: s.key as string, shortcut: s },
    ...(s.alsoAccepts ?? []).map((combo) => ({ combo, shortcut: s })),
  ]);

/** Guard rail 2 — focus is somewhere typing happens. */
export function isEditingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  const el = target as HTMLElement;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable) return true;
  if (el.getAttribute('role') === 'textbox') return true;
  // An open AG-Grid cell editor: the editing cell carries .ag-cell-inline-editing, and a
  // popup editor lives in .ag-popup-editor outside the cell.
  return el.closest('.ag-cell-inline-editing, .ag-popup-editor, .ag-cell-editor') !== null;
}

/** Guard rail 3 — a modal is on screen. */
function dialogIsOpen(): boolean {
  return document.querySelector('[role="dialog"], [role="alertdialog"], dialog[open]') !== null;
}

function topEnabled(action: ShortcutActionId): Registration | undefined {
  const stack = stacks.get(action);
  if (!stack) return undefined;
  for (let i = stack.length - 1; i >= 0; i -= 1) {
    const entry = stack[i];
    if (entry?.enabled) return entry;
  }
  return undefined;
}

function onKeyDown(e: KeyboardEvent): void {
  // A repeat is PB's key-held case; the menu accelerator fires once, so ignore auto-repeat.
  if (e.repeat) return;
  for (const { combo, shortcut } of COMBO_TABLE) {
    if (!comboMatches(combo, e)) continue;
    const entry = topEnabled(shortcut.action);
    // Guard rail 1: nothing bound -> the browser keeps the key.
    if (!entry) continue;
    if (!shortcut.allowInEditor && isEditingTarget(e.target)) continue;
    if (shortcut.blockedByDialog && dialogIsOpen()) continue;
    if (shortcut.preventDefault !== false) e.preventDefault();
    entry.run(e);
    return;
  }
}

let listening = false;

function syncListener(): void {
  const wanted = stacks.size > 0;
  if (wanted === listening) return;
  if (wanted) window.addEventListener('keydown', onKeyDown);
  else window.removeEventListener('keydown', onKeyDown);
  listening = wanted;
}

/**
 * Bind a handler to a PB action for as long as the returned unsubscribe is uncalled.
 *
 * <p>Imperative twin of {@link useShortcutAction} — for non-React callers and for the
 * app-chrome bindings in `appShortcuts.ts`. Registering an action whose key is `null`
 * (skipped keys, mouse gestures) is legal and simply never fires: the caller does not have to
 * know which of PB's 38 keys survived the browser.
 */
export function registerShortcut(
  action: ShortcutActionId,
  run: (event: KeyboardEvent) => void,
  options: ShortcutHandlerOptions = {},
): () => void {
  // Throws on an unknown id rather than registering a handler that can never fire.
  pbShortcut(action);
  const entry: Registration = { action, run, enabled: options.enabled !== false };
  const stack = stacks.get(action);
  if (stack) stack.push(entry);
  else stacks.set(action, [entry]);
  syncListener();

  let released = false;
  return () => {
    if (released) return;
    released = true;
    const current = stacks.get(action);
    if (!current) return;
    const at = current.indexOf(entry);
    if (at >= 0) current.splice(at, 1);
    if (current.length === 0) stacks.delete(action);
    syncListener();
  };
}

/**
 * Run an action's handler as if its key had been pressed. Used by the toolbar tests and by
 * surfaces that offer the same action through a menu item.
 *
 * @returns true when a handler was registered and ran.
 */
export function triggerShortcutAction(action: ShortcutActionId): boolean {
  const entry = topEnabled(action);
  if (!entry) return false;
  entry.run(new KeyboardEvent('keydown'));
  return true;
}

/** Actions with at least one enabled handler right now — debug + test introspection. */
export function boundShortcutActions(): ShortcutActionId[] {
  return [...stacks.keys()].filter((a) => topEnabled(a) !== undefined).sort();
}

/** Test-only reset so one spec's stray registration cannot leak into the next. */
export function __resetShortcutRegistryForTests(): void {
  stacks.clear();
  syncListener();
}
